/**
 * The client-side contract for every API response. Follows the status
 * codes docs/architecture/error-handling.md defines (400/401/403/404/409/500)
 * and the resource-based, versioned API in docs/api/api-design.md (/api/v1).
 *
 * The exact shape is NOT a guess — docs/decisions/0005-api-response-standard.md
 * is an empty placeholder, but docs/notes/sprint1.3.md records the actual
 * standard that was meant to be extracted there and never was: a top-level
 * `message` on every response, `data` for the payload, `meta` reserved for
 * pagination, and errors as an `errors[]` array of `{field, message}` — not
 * the `{code, message, details}` shape this file previously assumed before
 * that note was found.
 */
export interface PaginationMeta {
  page: number;
  limit: number;
  totalItems: number;
  totalPages: number;
}

export interface ApiSuccessResponse<T> {
  success: true;
  message: string;
  data: T;
  meta?: PaginationMeta;
}

export interface ApiFieldError {
  field: string;
  message: string;
}

export interface ApiErrorResponse {
  success: false;
  message: string;
  errors?: ApiFieldError[];
}

export type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse;

/**
 * Thrown by the Axios response interceptor for every failed request, so
 * calling code (TanStack Query, form submit handlers, ...) always catches
 * one predictable error shape instead of branching on whether it received a
 * server error payload, a network failure, or a timeout. `fieldErrors` is
 * always an array (never undefined) so a form can do
 * `error.fieldErrors.find(e => e.field === "email")` without a null check.
 */
export class ApiError extends Error {
  readonly status: number | null;
  readonly fieldErrors: ApiFieldError[];

  constructor(message: string, options: { status: number | null; fieldErrors?: ApiFieldError[] }) {
    super(message);
    this.name = "ApiError";
    this.status = options.status;
    this.fieldErrors = options.fieldErrors ?? [];
  }
}
